/**
 * Dashboard Component
 * Coláiste Lurgan Management System - Analytics Dashboard
 * Card grid → click course → full detail view (same pattern as House Allocation)
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Link } from 'react-router-dom';
import { LoadingSpinner } from '../ui/loading';
import {
  Users,
  UserRound,
  Calendar,
  Bus,
  School,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Home,
  GraduationCap,
} from 'lucide-react';
import { useDashboardAnalytics } from '../../hooks/useDashboardAnalytics';
import { useClassAllocationSummary } from '../../hooks/useClassAllocation';
import { useUserPermissions } from '../../hooks/useAuth';
import { useCourseTime } from '../../contexts/CourseTimeContext';
import { CourseTimeToggle } from '../ui/course-time-toggle';
import InterviewerDashboard from './InterviewerDashboard';
import TeacherDashboard from './TeacherDashboard';
import HouseOwnerDashboard from './HouseOwnerDashboard';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatShortDate(dateString) {
  const date = new Date(dateString);
  return `${date.getDate()} ${date.toLocaleDateString('en-GB', { month: 'short' })}`;
}

function formatFullDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function StatRow({ label, value, valueClass = '' }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${valueClass}`}>{value}</span>
    </div>
  );
}

function ProgressRow({ label, labelClass, assigned, total, barClass, bgClass }) {
  const pct = total > 0 ? (assigned / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className={`text-sm w-14 flex-shrink-0 ${labelClass}`}>{label}</span>
      <div className={`flex-1 h-2 rounded-full ${bgClass}`}>
        <div
          className={`h-2 rounded-full transition-all ${barClass}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="text-sm text-muted-foreground w-16 text-right">{assigned}/{total}</span>
    </div>
  );
}

function StatPanel({ title, icon: Icon, iconClass = 'text-muted-foreground', children }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${iconClass}`} />
        <h3 className="font-medium text-sm">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

// ─── Course card (compact, grid view) ─────────────────────────────────────────

export function CourseCard({ course, onSelect, isSelected = false, showIndicator = true }) {
  const { t } = useTranslation();

  const isEnded = course.end_date && new Date(course.end_date) < new Date();

  const cinnireCount =
    (course.cinnire_breakdown?.male || 0) +
    (course.cinnire_breakdown?.female || 0) +
    (course.cinnire_breakdown?.other || 0);
  const studentCountExcl = course.student_count - cinnireCount;
  const capacityPct =
    course.capacity > 0 ? Math.round((course.student_count / course.capacity) * 100) : 0;
  const maleExcl = course.gender_breakdown.male - (course.cinnire_breakdown?.male || 0);
  const femaleExcl = course.gender_breakdown.female - (course.cinnire_breakdown?.female || 0);
  const malePct = studentCountExcl > 0 ? (maleExcl / studentCountExcl) * 100 : 0;
  const femalePct = studentCountExcl > 0 ? (femaleExcl / studentCountExcl) * 100 : 0;

  return (
    <button
      onClick={onSelect}
      className={`group text-left rounded-xl border transition-all w-full overflow-hidden ${
        isSelected
          ? 'border-amber-300 ring-1 ring-amber-300 shadow-md'
          : 'bg-card hover:border-border hover:shadow-md'
      } ${isEnded && !isSelected ? 'opacity-60' : ''}`}
    >
      {isSelected && (
        <div className="h-1 w-full" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }} />
      )}
      <div className="p-6">
      {/* Title + count */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-lg leading-tight">{course.name}</span>
            {isEnded && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground">
                {t('courseFilter.ended')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-muted-foreground">
            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
            <span>{formatShortDate(course.start_date)} – {formatShortDate(course.end_date)}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-3xl font-bold text-primary">{studentCountExcl}</div>
          <div className="text-xs text-muted-foreground">{t('dashboard.students')}</div>
        </div>
      </div>

      {/* Capacity bar */}
      <div className="space-y-1 mb-4">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{t('dashboard.capacity')}</span>
          <span>{course.student_count}/{course.capacity} · {capacityPct}%</span>
        </div>
        <div className="h-2 rounded-full bg-muted">
          <div
            className={`h-2 rounded-full transition-all ${capacityPct >= 100 ? 'bg-green-500' : 'bg-primary'}`}
            style={{ width: `${Math.min(capacityPct, 100)}%` }}
          />
        </div>
      </div>

      {/* M / F split */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-sm text-blue-600 w-14 flex-shrink-0">{t('dashboard.male')}</span>
          <div className="flex-1 h-2 rounded-full bg-blue-100 dark:bg-blue-900/40">
            <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${malePct}%` }} />
          </div>
          <span className="text-sm text-muted-foreground w-8 text-right">{maleExcl}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-pink-600 w-14 flex-shrink-0">{t('dashboard.female')}</span>
          <div className="flex-1 h-2 rounded-full bg-pink-100 dark:bg-pink-900/40">
            <div className="h-2 rounded-full bg-pink-500 transition-all" style={{ width: `${femalePct}%` }} />
          </div>
          <span className="text-sm text-muted-foreground w-8 text-right">{femaleExcl}</span>
        </div>
      </div>

      {/* Expand indicator — hidden on sidebar cards */}
      {showIndicator && (
        <div className="flex items-center justify-end gap-1 mt-4 pt-3 border-t text-muted-foreground/40 group-hover:text-muted-foreground transition-colors">
          <span className="text-xs">{t('dashboard.viewDetails')}</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </div>
      )}
      </div>
    </button>
  );
}

// ─── Course detail (full stats view) ─────────────────────────────────────────

function CourseDetail({ course, onBack, classSummary }) {
  const { t } = useTranslation();

  const cinnireCount =
    (course.cinnire_breakdown?.male || 0) +
    (course.cinnire_breakdown?.female || 0) +
    (course.cinnire_breakdown?.other || 0);
  const studentCountExcl = course.student_count - cinnireCount;
  const capacityPct =
    course.capacity > 0 ? Math.round((course.student_count / course.capacity) * 100) : 0;

  const placement = course.placement_breakdown || {};
  const payments = course.payment_breakdown || {};

  const totalMale = course.gender_breakdown.male;
  const totalFemale = course.gender_breakdown.female;
  const maleExcl = totalMale - (course.cinnire_breakdown?.male || 0);
  const femaleExcl = totalFemale - (course.cinnire_breakdown?.female || 0);

  const totalUnassigned = (placement.unassigned_male || 0) + (placement.unassigned_female || 0);

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div>
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 -ml-1 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('dashboard.allCourses')}
        </button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>{course.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>{formatFullDate(course.start_date)} – {formatFullDate(course.end_date)}</span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-4xl font-bold text-primary">{studentCountExcl}</div>
            <div className="text-sm text-muted-foreground">{t('dashboard.students')}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {t('dashboard.withCinnire')}: {course.student_count}
            </div>
          </div>
        </div>
      </div>

      {/* Stat panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

        {/* Students */}
        <StatPanel title={t('dashboard.students')} icon={Users}>
          <div className="space-y-1 mb-3">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t('dashboard.capacity')}</span>
              <span>{course.student_count}/{course.capacity} · {capacityPct}%</span>
            </div>
            <div className="h-2 rounded-full bg-muted">
              <div
                className={`h-2 rounded-full transition-all ${capacityPct >= 100 ? 'bg-green-500' : 'bg-primary'}`}
                style={{ width: `${Math.min(capacityPct, 100)}%` }}
              />
            </div>
          </div>
          <StatRow
            label={t('dashboard.cinnire')}
            value={cinnireCount}
            valueClass="text-amber-600"
          />
          {course.cancelled_count > 0 && (
            <StatRow
              label={t('dashboard.cancelled')}
              value={course.cancelled_count}
              valueClass="text-red-600"
            />
          )}
        </StatPanel>

        {/* Gender */}
        <StatPanel title={t('dashboard.genderSplit')} icon={UserRound}>
          <ProgressRow
            label={t('dashboard.male')}
            labelClass="text-blue-600"
            assigned={maleExcl}
            total={studentCountExcl}
            barClass="bg-blue-500"
            bgClass="bg-blue-100 dark:bg-blue-900/40"
          />
          <ProgressRow
            label={t('dashboard.female')}
            labelClass="text-pink-600"
            assigned={femaleExcl}
            total={studentCountExcl}
            barClass="bg-pink-500"
            bgClass="bg-pink-100 dark:bg-pink-900/40"
          />
          <div className="pt-2 mt-1 border-t space-y-2">
            <div className="text-xs text-muted-foreground font-medium">{t('dashboard.cinnire')}</div>
            <StatRow label={t('dashboard.male')} value={course.cinnire_breakdown?.male || 0} valueClass="text-amber-600" />
            <StatRow label={t('dashboard.female')} value={course.cinnire_breakdown?.female || 0} valueClass="text-amber-600" />
          </div>
        </StatPanel>

        {/* Interviews */}
        <StatPanel title={t('dashboard.interviews')} icon={ClipboardList}>
          <StatRow
            label={t('interviewDashboard.pending')}
            value={course.interview_breakdown?.pending || 0}
            valueClass={(course.interview_breakdown?.pending || 0) > 0 ? 'text-amber-600' : 'text-green-600'}
          />
          <StatRow
            label={t('interviewDashboard.scored')}
            value={course.interview_breakdown?.scored || 0}
          />
          <StatRow
            label={t('interviewDashboard.notRequired')}
            value={course.interview_breakdown?.not_required || 0}
            valueClass="text-muted-foreground"
          />
        </StatPanel>

        {/* Housing */}
        <StatPanel title={t('dashboard.placement')} icon={Home} iconClass="text-indigo-500">
          <ProgressRow
            label={t('dashboard.male')}
            labelClass="text-blue-600"
            assigned={placement.assigned_male || 0}
            total={totalMale}
            barClass="bg-blue-500"
            bgClass="bg-blue-100 dark:bg-blue-900/40"
          />
          <ProgressRow
            label={t('dashboard.female')}
            labelClass="text-pink-600"
            assigned={placement.assigned_female || 0}
            total={totalFemale}
            barClass="bg-pink-500"
            bgClass="bg-pink-100 dark:bg-pink-900/40"
          />
          <div className="pt-2 mt-1 border-t">
            <StatRow
              label={t('dashboard.unassigned')}
              value={totalUnassigned}
              valueClass={totalUnassigned > 0 ? 'text-amber-600' : 'text-green-600'}
            />
          </div>
        </StatPanel>

        {/* Payments */}
        <StatPanel title={t('dashboard.payments')} icon={CreditCard} iconClass="text-emerald-500">
          <StatRow
            label={t('dashboard.fullyPaid')}
            value={payments.fully_paid || 0}
            valueClass="text-green-600"
          />
          <StatRow
            label={t('dashboard.outstanding')}
            value={payments.outstanding || 0}
            valueClass={(payments.outstanding || 0) > 0 ? 'text-amber-600' : 'text-muted-foreground'}
          />
          <StatRow
            label={t('dashboard.noPayment')}
            value={payments.no_payment || 0}
            valueClass="text-muted-foreground"
          />
        </StatPanel>

        {/* School Years */}
        {course.school_year_breakdown && Object.keys(course.school_year_breakdown).length > 0 && (
          <StatPanel title={t('dashboard.schoolYears')} icon={School}>
            <div className="flex justify-between text-xs text-muted-foreground pb-1 border-b">
              <span className="flex-1" />
              <span className="w-10 text-center text-blue-600">M</span>
              <span className="w-10 text-center text-pink-600">F</span>
              <span className="w-12 text-right">{t('dashboard.total', 'Total')}</span>
            </div>
            {Object.entries(course.school_year_breakdown).map(([year, data]) => (
              <div key={year} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex-1">{year}</span>
                <span className="w-10 text-center text-blue-600">{data.male}</span>
                <span className="w-10 text-center text-pink-600">{data.female}</span>
                <span className="w-12 text-right font-medium">{data.total}</span>
              </div>
            ))}
          </StatPanel>
        )}

        {/* Travel */}
        {course.travel_required > 0 && (
          <StatPanel title={t('dashboard.travel')} icon={Bus}>
            <StatRow label={t('dashboard.studentsRequireTravel')} value={course.travel_required} />
            {Object.entries(course.travel_breakdown || {}).map(([location, count]) => (
              <StatRow key={location} label={location} value={count} />
            ))}
          </StatPanel>
        )}

        {/* Classes */}
        {classSummary && (
          <StatPanel title={t('dashboard.classes')} icon={GraduationCap} iconClass="text-violet-500">
            <div className="space-y-1 mb-3">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{t('classAllocation.students')}</span>
                <span>
                  {classSummary.assigned}/{classSummary.total} ·{' '}
                  {classSummary.total > 0 ? Math.round((classSummary.assigned / classSummary.total) * 100) : 0}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted">
                <div
                  className="h-2 rounded-full bg-violet-500 transition-all"
                  style={{ width: `${classSummary.total > 0 ? Math.min((classSummary.assigned / classSummary.total) * 100, 100) : 0}%` }}
                />
              </div>
            </div>
            <StatRow
              label={t('classAllocation.unassigned')}
              value={classSummary.unassigned}
              valueClass={classSummary.unassigned > 0 ? 'text-amber-600' : 'text-green-600'}
            />
            <StatRow label={t('dashboard.classGroups')} value={classSummary.class_count} />
            <div className="pt-2 mt-1 border-t">
              <Link
                to="/class-allocation"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {t('dashboard.viewAllocationBoard')}
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
          </StatPanel>
        )}
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export function Dashboard() {
  const { t } = useTranslation();
  const { userType } = useUserPermissions();
  const [selectedCourse, setSelectedCourse] = useState(null);

  const { showAllTime } = useCourseTime();
  const isInterviewer = userType === 'INTERVIEWER';
  const isParent = userType === 'PARENT';
  const isTeacher = userType === 'TEACHER';
  const isHouseOwner = userType === 'HOUSE_OWNER';

  const { data: analytics, isLoading, error } = useDashboardAnalytics(null, {
    enabled: !isInterviewer && !isParent && !isTeacher && !isHouseOwner,
  });

  const { data: classSummaryList } = useClassAllocationSummary();

  // Role redirects (hooks must be called above)
  if (isParent) return <Navigate to="/family-dashboard" replace />;
  if (isInterviewer) return <InterviewerDashboard />;
  if (isTeacher) return <TeacherDashboard />;
  if (isHouseOwner) return <HouseOwnerDashboard />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive">Error loading dashboard data</p>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  const { courses } = analytics || {};

  const selectedCourseData = courses?.find((c) => c.uuid === selectedCourse);

  // Dynamic grid cols (same logic as House Allocation)
  const n = courses?.length || 0;
  const cols =
    n <= 2 ? 2 :
    n === 3 ? 3 :
    n === 4 ? 2 :
    n <= 6 ? 3 : 4;
  const gridClass =
    cols === 2 ? 'grid-cols-2 max-w-3xl' :
    cols === 3 ? 'grid-cols-3 max-w-5xl' :
                 'grid-cols-4 max-w-7xl';

  const otherCourses = courses?.filter((c) => c.uuid !== selectedCourse) || [];

  return (
    <div className="pb-8">
      {selectedCourse && selectedCourseData ? (
        <div className="flex gap-5 items-start">
          {/* Expanded detail card — re-keyed on course switch so it re-animates */}
          <div
            key={selectedCourse}
            className="flex-1 min-w-0 fade-in"
          >
            <CourseDetail
              course={selectedCourseData}
              onBack={() => setSelectedCourse(null)}
              classSummary={classSummaryList?.find((c) => c.uuid === selectedCourse)}
            />
          </div>
          {/* Other courses stacked in sidebar — slides in once on first open */}
          {otherCourses.length > 0 && (
            <div className="w-72 flex-shrink-0 space-y-3 mt-14 fade-in">
              {otherCourses.map((course) => (
                <CourseCard
                  key={course.uuid}
                  course={course}
                  onSelect={() => setSelectedCourse(course.uuid)}
                  showIndicator={false}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="fade-in">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>{t('dashboard.courseOverview')}</h1>
              <p className="text-muted-foreground mt-1 pl-3">{t('dashboard.subtitle')}</p>
            </div>
            <CourseTimeToggle />
          </div>
          {courses?.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground">
              {showAllTime ? t('dashboard.noCoursesAllTime') : t('dashboard.noCourses')}
            </div>
          ) : (
            <div className={`grid gap-5 mx-auto w-full ${gridClass}`}>
              {courses?.map((course) => (
                <CourseCard
                  key={course.uuid}
                  course={course}
                  onSelect={() => setSelectedCourse(course.uuid)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
